import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Sparkles, BookOpen, LayoutDashboard, BarChart3, Settings, Users,
  Brain, Lock, Workflow, Plug,
} from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import SolutionCarousel from '@/components/layout/SolutionCarousel';

const inclusions = [
  { icon: Brain,          text: 'Created & trained for your business' },
  { icon: LayoutDashboard,text: 'Custom dashboard access' },
  { icon: Settings,       text: 'CRM integration (Zoho, Agile, HubSpot, Salesforce)' },
  { icon: Plug,           text: 'Automation & systems setup (workflows, integrations, notifications)' },
  { icon: BookOpen,       text: 'Managed onboarding & setup' },
  { icon: BarChart3,      text: 'Monthly reporting & analytics' },
  { icon: Sparkles,       text: 'Ongoing AI tuning & optimisation' },
  { icon: Users,          text: 'Human escalation paths' },
  { icon: Lock,           text: 'Data privacy' },
];

/* ── Page ── */
export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-24">
      <Helmet>
        <title>Custom AI & Automation Solutions — custum.ai</title>
        <meta name="description" content="Custom-built AI Callers, AI Receptionists, and AI Webchat agents — plus the automation that connects them to your business. CRM integration, fully managed." />
        <meta property="og:title" content="custum.ai — Custom AI & Automation Solutions" />
        <meta property="og:description" content="Every AI & automation solution is custom-built for your business. AI Caller, AI Receptionist, AI Webchat, and the systems that connect them — get a tailored quote." />
        <link rel="canonical" href="https://custum.ai/services/" />
      </Helmet>

      {/* Hero + Carousel */}
      <section className="py-24 md:py-8 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="solution-center"
          >
            <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-blue-light mb-4">
              CUSTOM SOLUTIONS
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-brand-white tracking-tight mb-4">
              Tailored AI &amp; Automation For <span className="text-brand-blue">Your Business</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-lg mx-auto">
              Every solution is custom-built, trained on your data, and connected to your systems through automation. No one-size-fits-all.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <SolutionCarousel />
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section data-testid="inclusions-section" className="py-24 md:py-28 bg-brand-charcoal">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold text-brand-white tracking-tight">
              Every Solution Includes
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            {inclusions.map((item) => (
              <motion.div key={item.text} variants={fadeInUp} className="flex items-center gap-3 text-sm text-brand-off-white">
                <item.icon size={20} className="text-brand-blue flex-shrink-0" />
                {item.text}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Automation Explainer */}
      <section data-testid="automation-explainer-section" className="py-24 md:py-32 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-blue-light mb-4">
              AUTOMATION
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-white tracking-tight mb-4">
              What "Automation" Means For Us
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-2xl mx-auto leading-relaxed">
              Automation means bringing automation and systems into your existing business — not just dropping an AI on top of it. It improves your team's productivity and connects everything to the AI we build for you. In practice, that service covers two things:
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} data-testid="automation-explainer-connect" className="glass-card glass-card-hover p-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
                <Plug size={24} className="text-brand-blue" />
              </div>
              <h3 className="text-xl font-bold text-brand-white mb-3">Connecting to Your Existing Systems</h3>
              <p className="text-sm text-brand-muted leading-relaxed">
                We connect our AI solution to the systems you already run your business on — your CRM, calendar, inbox, and other tools — so the AI works with what you have, and nothing falls through the cracks.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} data-testid="automation-explainer-build" className="glass-card glass-card-hover p-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
                <Workflow size={24} className="text-brand-blue" />
              </div>
              <h3 className="text-xl font-bold text-brand-white mb-3">Building New Systems Where Needed</h3>
              <p className="text-sm text-brand-muted leading-relaxed">
                If the right systems aren't in place yet, we build them — setting up CRMs, integrations and connections, automated workflows, and notifications — so your business has something solid for the AI to plug into.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
